import { useCallback, useRef } from 'react';

interface UseLongPressOptions {
  /** Hold duration before the press fires. */
  delayMs?: number;
  /** Movement (px) that cancels the press as a scroll/drag. */
  moveTolerance?: number;
}

interface LongPressHandlers {
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchMove: (e: React.TouchEvent) => void;
  onTouchEnd: (e: React.TouchEvent) => void;
  onTouchCancel: (e: React.TouchEvent) => void;
}

/**
 * Fires `onLongPress(x, y)` after the user holds a single-finger touch for
 * `delayMs` without moving more than `moveTolerance` px. Touch devices have no
 * right-click, so this is how the message context menu is surfaced on mobile.
 *
 * The press is cancelled by movement (a scroll/drag), an early release, a
 * second finger, or a cancelled touch. When it fires we `preventDefault()` on
 * the following `touchend` to suppress the browser's synthetic mouse events
 * (mousedown/click) — otherwise the phantom tap would immediately dismiss the
 * just-opened menu or land on a menu item.
 */
export function useLongPress(
  onLongPress: (x: number, y: number) => void,
  { delayMs = 450, moveTolerance = 10 }: UseLongPressOptions = {},
): LongPressHandlers {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const firedRef = useRef(false);

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    startRef.current = null;
  }, []);

  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length !== 1) {
        cancel();
        return;
      }
      const t = e.touches[0];
      startRef.current = { x: t.clientX, y: t.clientY };
      firedRef.current = false;
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        const s = startRef.current;
        if (!s) return;
        firedRef.current = true;
        onLongPress(s.x, s.y);
      }, delayMs);
    },
    [cancel, delayMs, onLongPress],
  );

  const onTouchMove = useCallback(
    (e: React.TouchEvent) => {
      const s = startRef.current;
      if (!s) return;
      const t = e.touches[0];
      if (Math.abs(t.clientX - s.x) > moveTolerance || Math.abs(t.clientY - s.y) > moveTolerance) {
        cancel();
      }
    },
    [cancel, moveTolerance],
  );

  const onTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      cancel();
      if (firedRef.current) {
        firedRef.current = false;
        e.preventDefault();
      }
    },
    [cancel],
  );

  return {
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onTouchCancel: onTouchEnd,
  };
}
